import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Users, UserPlus, Edit2, Trash2, Building, Phone, Mail } from 'lucide-react';
import { COMPANY } from '../../../config/company';

// Contact roles configuration
const CONTACT_ROLES = {
  homeowner: { label: 'Homeowner', labelEs: 'Propietario', color: '#3b82f6' },
  project_manager: { label: 'Project Manager', labelEs: 'Gerente de Proyecto', color: '#8b5cf6' },
  architect: { label: 'Architect', labelEs: 'Arquitecto', color: '#6366f1' },
  engineer: { label: 'Engineer', labelEs: 'Ingeniero', color: '#14b8a6' },
  inspector: { label: 'Inspector', labelEs: 'Inspector', color: '#f59e0b' },
  hoa_rep: { label: 'HOA Representative', labelEs: 'Representante HOA', color: '#ec4899' },
  tenant: { label: 'Tenant', labelEs: 'Inquilino', color: '#06b6d4' },
  property_manager: { label: 'Property Manager', labelEs: 'Administrador', color: '#10b981' },
  other: { label: 'Other', labelEs: 'Otro', color: '#6b7280' }
};

const LeadContactsTab = ({
  language,
  contacts,
  openContactModal,
  deleteContact
}) => {
  return (
    <Card className="border-gray-200" data-testid="lead-contacts-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center text-gray-900">
          <Users className="mr-2 h-5 w-5" />
          {language === 'es' ? 'Contactos del Proyecto' : 'Project Contacts'}
        </CardTitle>
        <Button 
          onClick={() => openContactModal()} 
          style={{ backgroundColor: COMPANY.primaryColor }} 
          className="text-white"
          data-testid="add-contact-btn"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Agregar Contacto' : 'Add Contact'}
        </Button>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {language === 'es' 
                ? 'No hay contactos para este proyecto todavía'
                : 'No contacts for this project yet'}
            </p>
            <Button onClick={() => openContactModal()} variant="outline">
              <UserPlus className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Agregar Primer Contacto' : 'Add First Contact'}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map(contact => {
              const roleInfo = CONTACT_ROLES[contact.role] || CONTACT_ROLES.other;
              return (
                <div 
                  key={contact.id} 
                  className="p-4 rounded-lg border bg-white hover:shadow-md transition-shadow"
                  data-testid={`contact-card-${contact.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold" 
                        style={{ backgroundColor: roleInfo.color }}
                      >
                        {contact.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{contact.name}</p>
                        <Badge className="text-white text-xs" style={{ backgroundColor: roleInfo.color }}>
                          {language === 'es' ? roleInfo.labelEs : roleInfo.label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => openContactModal(contact)} 
                        className="h-8 w-8 p-0 text-blue-500"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => deleteContact(contact.id)} 
                        className="h-8 w-8 p-0 text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {contact.company && (
                    <p className="text-sm text-gray-600 flex items-center gap-1 mb-1">
                      <Building className="h-3 w-3" />{contact.company}
                    </p>
                  )}
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone}`} 
                      className="text-sm text-blue-600 flex items-center gap-1 mb-1 hover:underline"
                    >
                      <Phone className="h-3 w-3" />{contact.phone}
                    </a>
                  )}
                  {contact.email && (
                    <a 
                      href={`mailto:${contact.email}`} 
                      className="text-sm text-blue-600 flex items-center gap-1 hover:underline"
                    >
                      <Mail className="h-3 w-3" />{contact.email}
                    </a>
                  )}
                  {contact.notes && (
                    <p className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">{contact.notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadContactsTab;
